/**
 * Lagoon public GraphQL API client.
 *
 * One batched query returns every visible Lagoon vault on a chain with
 * its full state (NAV, totalAssets/Supply, time-weighted APR windows,
 * deposit-mode flags). This is the data-of-record for Lagoon: pricing
 * and yield come straight from the API, so the public-data fetch needs
 * no multicall and no archival RPC (Lagoon's on-chain history is only
 * available via pruned RPCs / the subgraph — the API serves it
 * directly).
 *
 * Endpoint and chain coverage:
 *   https://api.lagoon.finance/query  (GraphQL, no auth)
 * Chains are gated by {@link LAGOON_CHAIN_IDS} so we don't fire a query
 * at chains where Lagoon has no factory. The API itself returns an empty
 * page for an unknown chain, but short-circuiting avoids the round-trip.
 */

export const LAGOON_API_URL = 'https://api.lagoon.finance/query'

/**
 * Chains with a Lagoon vault factory (per
 * docs.lagoon.finance/resources/networks-and-addresses). Numeric chain
 * ids as strings. Extend as Lagoon deploys to more chains — the parser
 * is chain-agnostic, this set only decides whether to issue the query.
 */
export const LAGOON_CHAIN_IDS = new Set<string>([
  '1', // Ethereum
  '42161', // Arbitrum
  '8453', // Base
  '43114', // Avalanche
  '10', // Optimism
  '137', // Polygon
  '59144', // Linea
  '5000', // Mantle
  '146', // Sonic
])

export const hasLagoonVaults = (chainId: string): boolean =>
  LAGOON_CHAIN_IDS.has(String(chainId))

/** Shape of a single `vaults.items[]` entry in the API response. */
export interface LagoonApiVault {
  address: string | null
  name: string | null
  symbol: string | null
  decimals: number | null
  isVisible: boolean | null
  asset: {
    address: string | null
    symbol: string | null
    decimals: number | null
  } | null
  /** Curator(s) running the vault; first entry is the primary. */
  curators: ({ id: string | null; name: string | null } | null)[] | null
  state: {
    totalAssets: string | null
    totalAssetsUsd: number | null
    totalSupply: string | null
    pricePerShare: string | null
    pricePerShareUsd: number | null
    isAsyncOnly: boolean | null
    syncMode: string | null
    weeklyApr: { twrrNetApr: number | null } | null
    monthlyApr: { twrrNetApr: number | null } | null
    yearlyApr: { twrrNetApr: number | null } | null
    inceptionApr: { twrrNetApr: number | null } | null
  } | null
}

const VAULTS_QUERY = `
query LagoonVaults($where: VaultFilterInput!, $first: Int!, $skip: Int!) {
  vaults(
    first: $first
    skip: $skip
    orderBy: totalAssetsUsd
    orderDirection: desc
    where: $where
  ) {
    items {
      address
      name
      symbol
      decimals
      curators {
        id
        name
      }
      isVisible
      asset {
        address
        symbol
        decimals
      }
      state {
        totalAssets
        totalAssetsUsd
        totalSupply
        pricePerShare
        pricePerShareUsd
        isAsyncOnly
        syncMode
        weeklyApr {
          twrrNetApr
        }
        monthlyApr {
          twrrNetApr
        }
        yearlyApr {
          twrrNetApr
        }
        inceptionApr {
          twrrNetApr
        }
      }
    }
  }
}`

const PAGE_SIZE = 200

/**
 * Fetch every visible Lagoon vault on a chain, paginating until the API
 * returns a short page. Returns the raw API items; normalization to
 * `LagoonVault` happens in `fetchPublic`.
 */
export async function fetchLagoonApiVaults(
  chainId: string,
): Promise<LagoonApiVault[]> {
  const out: LagoonApiVault[] = []
  let skip = 0
  // Hard cap on pages so a misbehaving API can't loop forever.
  for (let page = 0; page < 20; page++) {
    const response = await fetch(LAGOON_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: VAULTS_QUERY,
        variables: {
          where: { chainId_eq: Number(chainId), isVisible_eq: true },
          first: PAGE_SIZE,
          skip,
        },
      }),
    })
    if (!response.ok) {
      throw new Error(
        `Lagoon vaults API failed: ${response.status} - ${response.statusText}`,
      )
    }
    const json: any = await response.json()
    if (json?.errors?.length) {
      throw new Error(
        `Lagoon vaults API error: ${json.errors[0]?.message ?? 'unknown'}`,
      )
    }
    const items = (json?.data?.vaults?.items ?? []) as LagoonApiVault[]
    out.push(...items)
    if (items.length < PAGE_SIZE) break
    skip += PAGE_SIZE
  }
  return out
}
